import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDate, formatDateTime } from "@/i18n/format";
import { getApiErrorMessage } from "@/lib/apiError";
import { formatMinutes, isCoordinatorQuery, stallRuleLabel } from "@/lib/coordinator";
import {
  useModeratorsCoordinatorDealsRetrieve,
  useModeratorsCoordinatorDealsTimeLogsCreate,
  useModeratorsCoordinatorTimeLogsDestroy,
} from "@/services/moderators/generated/moderators-coordinator/moderators-coordinator";
import CoordinatorDraftCard from "./CoordinatorDraftCard";
import DealCoordinatorInsights from "./DealCoordinatorInsights";
import "./Coordinator.css";

/**
 * WP2.6 — deal coordinator panel on the moderator deal detail page:
 * drafts waiting for approval for this deal and the moderator time log.
 */
const DealCoordinatorPanel = ({
  dealId,
  currentModeratorId,
  isSuper,
}: {
  dealId: number;
  currentModeratorId: number;
  isSuper: boolean;
}) => {
  const { t } = useTranslation(["deals", "common"]);
  const qc = useQueryClient();
  const { data: panel, isLoading, error } = useModeratorsCoordinatorDealsRetrieve(dealId);
  const refresh = () => qc.invalidateQueries({ predicate: (q) => isCoordinatorQuery(q.queryKey) });
  const logMutation = useModeratorsCoordinatorDealsTimeLogsCreate({ mutation: { onSuccess: refresh } });
  const deleteMutation = useModeratorsCoordinatorTimeLogsDestroy({ mutation: { onSuccess: refresh } });

  const [minutes, setMinutes] = useState("");
  const [note, setNote] = useState("");
  const [date, setDate] = useState("");
  const [logError, setLogError] = useState<string | null>(null);

  if (isLoading) return <section className="mod-page__card">{t("common:states.loading")}</section>;
  if (error || !panel) {
    return (
      <section className="mod-page__card">
        <p className="mod-page__error">{t("coordinator.panel.loadError")}</p>
      </section>
    );
  }

  const pendingDrafts = panel.drafts.filter((d) => d.status === "pending");
  const reviewedDrafts = panel.drafts.filter((d) => d.status !== "pending");

  const submitTime = () => {
    const mins = Number(minutes);
    if (!Number.isInteger(mins) || mins < 1 || mins > 1440) {
      setLogError(t("coordinator.timeLog.errors.minutes"));
      return;
    }
    logMutation.mutate(
      { id: dealId, data: { minutes: mins, note: note.trim(), ...(date ? { date } : {}) } },
      {
        onSuccess: () => {
          setMinutes("");
          setNote("");
          setDate("");
          setLogError(null);
        },
        onError: (err) => setLogError(getApiErrorMessage(err, t("coordinator.timeLog.errors.saveFailed"))),
      },
    );
  };

  return (
    <section className="mod-page__card">
      <div className="mod-page__card-header">
        <h2 className="mod-page__card-title">{t("coordinator.panel.title")}</h2>
      </div>
      <p className="mod-page__card-meta">{t("coordinator.panel.internalOnly")}</p>

      {panel.stallFlags.length > 0 ? (
        <div className="coord-panel__banner" role="status">
          <strong>{t("coordinator.stall.dealFlagged")}</strong>
          <ul className="coord-panel__list">
            {panel.stallFlags.map((f) => (
              <li key={f.id}>
                {stallRuleLabel(f.rule, f.days)} · {t("coordinator.stall.since", { date: formatDateTime(f.flaggedAt) })}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <DealCoordinatorInsights panel={panel} />

      <div className="coord-panel__section">
        <h3>{t("coordinator.panel.pendingDrafts", { count: pendingDrafts.length })}</h3>
        {pendingDrafts.length === 0 ? <p className="mod-page__empty">{t("coordinator.panel.noDrafts")}</p> : null}
        {pendingDrafts.map((d) => (
          <CoordinatorDraftCard key={d.id} draft={d} defaultReviewMinutes={panel.defaultReviewMinutes} />
        ))}
        {reviewedDrafts.length > 0 ? (
          <details>
            <summary>{t("coordinator.panel.reviewedDrafts", { count: reviewedDrafts.length })}</summary>
            {reviewedDrafts.map((d) => (
              <CoordinatorDraftCard key={d.id} draft={d} defaultReviewMinutes={panel.defaultReviewMinutes} />
            ))}
          </details>
        ) : null}
      </div>

      <div className="coord-panel__section">
        <h3>{t("coordinator.timeLog.title", { total: formatMinutes(panel.totalMinutes) })}</h3>
        <div className="mod-modal__row">
          <label className="mod-modal__label">
            {t("coordinator.timeLog.minutesLabel")}
            <input
              type="number"
              min={1}
              max={1440}
              className="mod-page__input"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
            />
          </label>
          <label className="mod-modal__label">
            {t("coordinator.timeLog.dateLabel")}
            <input type="date" className="mod-page__input" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label className="mod-modal__label">
            {t("coordinator.timeLog.noteLabel")}
            <input
              type="text"
              maxLength={500}
              className="mod-page__input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
        </div>
        <button
          type="button"
          className="mod-page__btn mod-page__btn--primary"
          disabled={logMutation.isPending}
          onClick={submitTime}
        >
          {logMutation.isPending ? t("common:actions.saving") : t("coordinator.timeLog.add")}
        </button>
        {logError ? <p className="mod-modal__error">{logError}</p> : null}

        {panel.timeLogs.length === 0 ? (
          <p className="mod-page__empty">{t("coordinator.timeLog.empty")}</p>
        ) : (
          <table className="coord-panel__table">
            <thead>
              <tr>
                <th>{t("coordinator.timeLog.columns.date")}</th>
                <th>{t("coordinator.timeLog.columns.moderator")}</th>
                <th>{t("coordinator.timeLog.columns.minutes")}</th>
                <th>{t("coordinator.timeLog.columns.note")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {panel.timeLogs.map((log) => (
                <tr key={log.id}>
                  <td>{formatDate(log.date)}</td>
                  <td>{log.moderatorName ?? "—"}</td>
                  <td>{log.minutes}</td>
                  <td>{log.note}</td>
                  <td>
                    {isSuper || log.moderatorId === currentModeratorId ? (
                      <button
                        type="button"
                        className="mod-page__btn mod-page__btn--ghost"
                        disabled={deleteMutation.isPending}
                        onClick={() => deleteMutation.mutate({ id: log.id })}
                      >
                        {t("common:actions.delete")}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
};

export default DealCoordinatorPanel;
